// @ts-nocheck
/*
 * Port of views/dashboard/ivr/voice-library/edit-voice.js (395 lines).
 * BYTE-IDENTICAL.
 *
 * Edits one prompt's language files. Requires `location.state.prompt` to be
 * seeded by the caller (the Edit action on the Voice Library list). If
 * arrived cold (no state), redirects with a toast.
 *
 * Only pairs with `status === PROMPT_STATUS.REJECT` or
 * `status === PROMPT_STATUS.PENDING` are editable; APPROVED files are shown
 * as read-only info. On save, each editable pair is POSTed to
 * `${BACKEND_API_BASE_URL}/v2/prompt/:id/language-file` â€” if user didn't
 * pick a new file, we RE-fetch the existing URL and re-upload as a
 * synthesized `${language}-existing` File (v1 quirk, preserved).
 *
 * v1 quirks preserved:
 *  - The `prompt_url` shape has two forms: array-of-entries or a single
 *    legacy string; the string form is coerced to `[{language:'hi', url, status: prompt.prompt_status || 0}]`.
 *  - `voice_type: '1'` maps to 'Incoming', `'2'` to 'Both' (v2 addition —
 *    not present in v1, which had no "Both" voice type), anything else to
 *    'Outgoing' (bare `String(prompt.voice_type)` comparison, so numeric
 *    or string forms both map correctly).
 *  - Filename must match `^[a-zA-Z0-9_-]+$` â€” same rule as Add.
 *  - Cancel navigates back to VIEW_VOICE_FILES (list), NOT to the origin
 *    that opened this page â€” same as v1.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
import { toast } from 'sonner';
import { FaPlay, FaPause } from 'react-icons/fa';
import { getApiCall, postApiMediaCall } from '@/service/httpCall';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ROUTES,
  PROMPT_STATUS,
  BACKEND_API_BASE_URL,
} from '@/utilities/constant';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

import { canUploadPromptsOrMoh } from '@/utilities/promptUploadAccess';
import { UploadNotPermitted } from '@/components/feedback/UploadNotPermitted';

const EditVoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const prompt = location.state?.prompt;

  const [fileName, setFileName] = useState('');
  const [fileNameError, setFileNameError] = useState('');
  const [voiceType, setVoiceType] = useState('');
  const [languages, setLanguages] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [langFilePairs, setLangFilePairs] = useState<any[]>([]);

  const [playingId, setPlayingId] = useState<any>(null);
  const audioRef = useRef<any>(null);
  const audioUrlRef = useRef<any>(null);

  useEffect(() => {
    if (!prompt) {
      toast.error(
        'Please open this page using the Edit icon from Voice List.',
      );
      navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`);
    }
  }, []);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePreview = (pair: any, source: any = 'file') => {
    const playId = `${pair.language}-${source}`;
    if (playingId === playId) {
      stopPreview();
      return;
    }
    let url;
    let isObjectUrl = false;
    if (source === 'file') {
      if (!pair.file) return;
      url = URL.createObjectURL(pair.file);
      isObjectUrl = true;
    } else {
      if (!pair.existingUrl) return;
      url = pair.existingUrl;
    }
    stopPreview();
    const audio = new Audio(url);
    audio.onended = stopPreview;
    audio.onerror = () => {
      toast.error('Unable to play selected audio.');
      stopPreview();
    };
    audioRef.current = audio;
    audioUrlRef.current = isObjectUrl ? url : null;
    audio
      .play()
      .then(() => setPlayingId(playId))
      .catch(() => stopPreview());
  };

  const downloadExistingFile = async (pair: any) => {
    if (!pair.existingUrl) return;
    try {
      const response = await fetch(pair.existingUrl);
      if (!response.ok) throw new Error('Failed to fetch the file');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName || 'voice_file'}_${pair.language}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Unable to download the existing file.');
    }
  };

  useEffect(() => () => stopPreview(), []);

  useEffect(() => {
    if (!prompt) return;

    setFileName(prompt.prompt_name || '');

    const vt = String(prompt.voice_type);
    setVoiceType(vt === '1' ? 'incoming' : vt === '2' ? 'both' : 'outgoing');

    const entries = Array.isArray(prompt.prompt_url)
      ? prompt.prompt_url
      : prompt.prompt_url
      ? [
          {
            language: 'hi',
            url: prompt.prompt_url,
            status: prompt.prompt_status || 0,
          },
        ]
      : [];

    setLangFilePairs(
      entries.map((e: any) => ({
        language: e.language,
        existingUrl: e.url,
        status: Number(e.status),
        file: null,
      })),
    );
  }, [prompt]);

  const fetchLanguages = () => {
    getApiCall('/v2/tts-languages').then((response: any) => {
      if (Array.isArray(response)) {
        const transformed = response.map((lang: any) => ({
          label: lang.language,
          value: lang.code,
        }));
        setLanguages(transformed);
      }
    });
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  const getLangLabel = (code: any) =>
    languages.find((l: any) => l.value === code)?.label || code;

  const validateFileName = (name: string) => {
    const validPattern = /^[a-zA-Z0-9_-]+$/;
    if (name.trim() === '') {
      setFileNameError('File name is required');
      return false;
    }
    if (name.includes(' ')) {
      setFileNameError('File name cannot contain spaces');
      return false;
    }
    if (!validPattern.test(name)) {
      setFileNameError(
        'File name can only contain letters, numbers, underscore (_), and hyphen (-)',
      );
      return false;
    }
    setFileNameError('');
    return true;
  };

  const handleFileNameChange = (e: any) => {
    const value = e.target.value;
    setFileName(value);
    if (value.trim() !== '') {
      validateFileName(value);
    } else {
      setFileNameError('');
    }
  };

  const updatePairFile = (language: any, file: any) => {
    if (playingId === language) stopPreview();
    setLangFilePairs((prev) =>
      prev.map((p: any) =>
        p.language === language ? { ...p, file } : p,
      ),
    );
  };

  const handleUpload = async () => {
    if (!fileName.trim()) {
      toast.error('Please fill the file name!');
      return;
    }
    if (!validateFileName(fileName)) {
      toast.error('Please fix the file name errors before uploading');
      return;
    }

    const editablePairs = langFilePairs.filter(
      (p: any) =>
        p.status === PROMPT_STATUS.REJECT ||
        p.status === PROMPT_STATUS.PENDING,
    );

    if (editablePairs.length === 0) {
      toast.error('No rejected or pending language file found to edit.');
      return;
    }

    setIsUploading(true);
    try {
      await Promise.all(
        editablePairs.map(async (pair: any) => {
          let fileToSend = pair.file;

          if (!fileToSend) {
            if (!pair.existingUrl) {
              throw new Error(
                `Please select a file for language: ${getLangLabel(pair.language)}`,
              );
            }
            const response = await fetch(pair.existingUrl);
            if (!response.ok) {
              throw new Error(
                `Unable to fetch the current file for language: ${getLangLabel(
                  pair.language,
                )}`,
              );
            }
            const blob = await response.blob();
            fileToSend = new File([blob], `${pair.language}-existing`, {
              type: blob.type,
            });
          }

          const formData = new FormData();
          formData.append('language', pair.language);
          formData.append('file', fileToSend);
          return postApiMediaCall(
            `${BACKEND_API_BASE_URL}/v2/prompt/${id}/language-file`,
            formData,
          );
        }),
      );

      toast.success(
        'Voice file updated successfully, submitted for review again.',
      );
      setTimeout(
        () => navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`),
        1200,
      );
    } catch (err: any) {
      toast.error(
        typeof err === 'string' ? err : err?.message || 'Update failed',
      );
    } finally {
      setIsUploading(false);
    }
  };

  if (!prompt) return null;

  return (
    <FillPage>
      <PageHeader
        title="Edit Voice File"
        description="Re-upload voice-file variants for languages currently rejected or pending review."
      />
      <Row>
        <Col sm="12">
          <Card>
            <Card.Body>
              <form>
                <Row className="mb-3">
                  <Col md="6">
                    <label htmlFor="fileName" className="form-label">
                      File Name{' '}
                      <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="fileName"
                      value={fileName}
                      onChange={handleFileNameChange}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault();
                      }}
                      onBlur={() => validateFileName(fileName)}
                      className="form-control"
                      placeholder="File Name (letters, numbers, _, - only)"
                      style={{
                        borderColor: fileNameError ? '#dc3545' : undefined,
                        backgroundColor: fileNameError ? '#fff5f5' : undefined,
                      }}
                    />
                    {fileNameError && (
                      <div
                        className="text-danger"
                        style={{
                          fontSize: '0.875rem',
                          marginTop: '0.25rem',
                        }}
                      >
                        {fileNameError}
                      </div>
                    )}
                  </Col>
                  <Col md="6">
                    <label className="form-label">Voice Type</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        voiceType === 'incoming'
                          ? 'Incoming'
                          : voiceType === 'both'
                          ? 'Both'
                          : 'Outgoing'
                      }
                      disabled
                    />
                  </Col>
                </Row>

                <div className="mb-2">
                  <label className="form-label fw-semibold">
                    Language & File
                  </label>
                </div>

                {langFilePairs.map((pair: any) => {
                  const isRejected = pair.status === PROMPT_STATUS.REJECT;
                  const isApproved = pair.status === PROMPT_STATUS.APPROVED;
                  const isEditable =
                    isRejected || pair.status === PROMPT_STATUS.PENDING;
                  const statusLabel = isRejected
                    ? 'Rejected'
                    : isApproved
                    ? 'Approved'
                    : 'Pending';
                  return (
                    <div
                      key={pair.language}
                      className="d-flex align-items-start gap-3 mb-3 p-3"
                      style={{
                        background: isRejected
                          ? '#fff5f5'
                          : isEditable
                          ? '#fffbeb'
                          : '#f8f9fa',
                        borderRadius: 8,
                        border: `1px solid ${
                          isRejected
                            ? '#fecaca'
                            : isEditable
                            ? '#fde68a'
                            : '#dee2e6'
                        }`,
                      }}
                    >
                      <div style={{ flex: '0 0 160px' }}>
                        <div
                          className="form-control"
                          style={{ background: '#eee' }}
                        >
                          {getLangLabel(pair.language)}
                        </div>
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: 6,
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            background: isRejected
                              ? '#fee2e2'
                              : isApproved
                              ? '#dcfce7'
                              : '#fef9c3',
                            color: isRejected
                              ? '#b91c1c'
                              : isApproved
                              ? '#15803d'
                              : '#a16207',
                          }}
                        >
                          {statusLabel}
                        </span>
                      </div>

                      {isEditable ? (
                        <div style={{ flex: 1 }}>
                          {pair.existingUrl && (
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <small className="text-muted">
                                Current file:
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                onClick={() => togglePreview(pair, 'existing')}
                                title={
                                  playingId === `${pair.language}-existing`
                                    ? 'Stop'
                                    : 'Listen'
                                }
                              >
                                {playingId === `${pair.language}-existing` ? (
                                  <FaPause size={11} />
                                ) : (
                                  <FaPlay size={11} />
                                )}
                                {playingId === `${pair.language}-existing`
                                  ? 'Stop'
                                  : 'Listen'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => downloadExistingFile(pair)}
                                title="Download"
                              >
                                Download
                              </button>
                            </div>
                          )}
                          <input
                            type="file"
                            className="form-control"
                            accept=".wav,.mp3"
                            onChange={(e: any) =>
                              updatePairFile(
                                pair.language,
                                e.target.files[0] || null,
                              )
                            }
                          />
                          <small className="text-muted d-block mt-1">
                            Only upload a new file if you want to replace the
                            current one above.
                          </small>
                          {pair.file && (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() => togglePreview(pair, 'file')}
                                title={
                                  playingId === `${pair.language}-file`
                                    ? 'Stop'
                                    : 'Listen'
                                }
                              >
                                {playingId === `${pair.language}-file` ? (
                                  <FaPause size={11} />
                                ) : (
                                  <FaPlay size={11} />
                                )}
                                {playingId === `${pair.language}-file`
                                  ? 'Stop'
                                  : 'Listen'}
                              </button>
                              <small className="text-muted d-block">
                                {pair.file.name}
                              </small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            flex: 1,
                            alignSelf: 'center',
                            color: '#64748b',
                            fontSize: 13,
                          }}
                        >
                          This language file is already approved, so it can't
                          be edited here.
                        </div>
                      )}
                    </div>
                  );
                })}

                <br />
                <Button
                  variant="primary"
                  className="mt-3"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? 'Updating...' : 'Update File'}
                </Button>
                <Button
                  variant="secondary"
                  className="mt-3"
                  style={{ marginLeft: 10 }}
                  onClick={() =>
                    navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`)
                  }
                  disabled={isUploading}
                >
                  Cancel
                </Button>
              </form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </FillPage>
  );
};

const EditVoiceGuarded = () => {
  if (!canUploadPromptsOrMoh()) {
    return <UploadNotPermitted title="Edit Voice File" what="voice files" />;
  }

  return <EditVoice />;
};

export default EditVoiceGuarded;
